/**
 * The pause dialog.
 *
 * Shown whenever play stops: the player asked, pointer lock ended, the tab was
 * backgrounded. Because losing pointer lock is one of those triggers, this doubles as the
 * "click to get back in" affordance on desktop.
 */

import { useEffect, useRef } from 'react';
import { SettingsPanel } from './SettingsPanel';
import { useFocusTrap } from './useFocusTrap';
import { useGameStore } from '../store/gameStore';

type Props = {
  onResume: () => void;
};

export function PauseOverlay({ onResume }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef);

  // Where a waiting update is actually taken. The pause menu is the one place the player
  // is already stopped, so restarting here costs them nothing they were in the middle of.
  const isUpdateReady = useGameStore((store) => store.isUpdateReady);
  const applyUpdate = useGameStore((store) => store.applyUpdate);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onResume();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onResume]);

  return (
    <div className="overlay">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pause-heading"
        className="overlay"
        style={{ position: 'static', background: 'transparent', padding: 0 }}
      >
        <h2 id="pause-heading">Paused</h2>
        <button type="button" className="button" onClick={onResume}>
          Resume
        </button>

        {isUpdateReady && (
          <button type="button" className="button button--update" onClick={() => applyUpdate?.()}>
            Restart to update
          </button>
        )}

        <SettingsPanel />
      </div>
    </div>
  );
}
